import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import styles from './HealthQuizPage.module.css';

interface QuizData {
  biologicalSex: string;
  age: number;
  heightFeet: number;
  heightInches: number;
  currentWeight: number;
  goal: string;
  targetWeight: number;
  activityLevel: string;
  eatingStyle: string;
  healthConsiderations: string[];
  eatingChallenges: string[];
  foodsToAvoid: string[];
  foodAllergies: string[];
}

const BIOLOGICAL_SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const GOAL_OPTIONS = [
  { value: 'lose_weight', label: 'Lose weight' },
  { value: 'maintain_weight', label: 'Maintain weight' },
  { value: 'gain_muscle', label: 'Gain muscle' },
  { value: 'improve_health', label: 'Improve overall health' },
  { value: 'more_energy', label: 'Have more energy' },
  { value: 'eat_healthier', label: 'Eat healthier' },
];

const ACTIVITY_LEVEL_OPTIONS = [
  { value: 'sedentary', label: 'Sedentary (little to no exercise)' },
  { value: 'lightly_active', label: 'Lightly active (1-2 days/week)' },
  { value: 'moderately_active', label: 'Moderately active (3-4 days/week)' },
  { value: 'very_active', label: 'Very active (5-6 days/week)' },
  { value: 'extremely_active', label: 'Extremely active (daily intense exercise)' },
];

const EATING_STYLE_OPTIONS = [
  { value: 'no_restrictions', label: 'No restrictions' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'keto_low_carb', label: 'Keto/Low-carb' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'flexitarian', label: 'Flexitarian' },
];

const HEALTH_CONSIDERATION_OPTIONS = [
  'Diabetes',
  'High blood pressure',
  'Heart disease',
  'High cholesterol',
  'Thyroid condition',
  'PCOS',
  'Digestive issues',
  'Autoimmune condition',
];

const EATING_CHALLENGE_OPTIONS = [
  'Lack of time',
  'Budget constraints',
  'Cravings',
  'Stress eating',
  'Social situations',
  'Cooking skills',
  'Picky eating',
  'Portion control',
];

const FOODS_TO_AVOID_OPTIONS = [
  'Red meat',
  'Pork',
  'Shellfish',
  'Eggs',
  'Dairy',
  'Gluten',
  'Soy',
  'Processed foods',
];

const FOOD_ALLERGY_OPTIONS = [
  'Peanuts',
  'Tree nuts',
  'Dairy',
  'Eggs',
  'Wheat/Gluten',
  'Soy',
  'Fish',
  'Shellfish',
];

const TOTAL_STEPS = 12;

export default function HealthQuizPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [quizData, setQuizData] = useState<QuizData>({
    biologicalSex: '',
    age: 35,
    heightFeet: 5,
    heightInches: 6,
    currentWeight: 150,
    goal: '',
    targetWeight: 150,
    activityLevel: '',
    eatingStyle: '',
    healthConsiderations: [],
    eatingChallenges: [],
    foodsToAvoid: [],
    foodAllergies: [],
  });

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleMultiSelect = (field: keyof QuizData, value: string) => {
    const currentArray = quizData[field] as string[];
    if (currentArray.includes(value)) {
      setQuizData({
        ...quizData,
        [field]: currentArray.filter((item) => item !== value),
      });
    } else {
      setQuizData({
        ...quizData,
        [field]: [...currentArray, value],
      });
    }
  };

  const handleComplete = async () => {
    if (!user) return;

    setIsSaving(true);
    setError('');

    try {
      const { error: upsertError } = await supabase
        .from('health_profiles')
        .upsert({
          user_id: user.id,
          biological_sex: quizData.biologicalSex,
          age_range: quizData.age.toString(),
          height_feet: quizData.heightFeet,
          height_inches: quizData.heightInches,
          current_weight: quizData.currentWeight,
          goal: quizData.goal,
          target_weight: quizData.targetWeight,
          activity_level: quizData.activityLevel,
          eating_style: quizData.eatingStyle,
          health_considerations: quizData.healthConsiderations,
          eating_challenges: quizData.eatingChallenges,
          foods_to_avoid: quizData.foodsToAvoid,
          food_allergies: quizData.foodAllergies,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (upsertError) throw upsertError;
      navigate('/health-info');
    } catch (err) {
      setError('Failed to save your health information. Please try again.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return quizData.biologicalSex !== '';
      case 2: return quizData.age >= 18 && quizData.age <= 100;
      case 3: return quizData.heightFeet >= 3 && quizData.heightFeet <= 8;
      case 4: return quizData.currentWeight >= 50 && quizData.currentWeight <= 500;
      case 5: return quizData.goal !== '';
      case 6: return quizData.targetWeight >= 50 && quizData.targetWeight <= 500;
      case 7: return quizData.activityLevel !== '';
      case 8: return quizData.eatingStyle !== '';
      case 9: return true;
      case 10: return true;
      case 11: return true;
      case 12: return true;
      default: return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your biological sex?</h2>
            <p className={styles.stepDescription}>This helps us calculate your nutritional needs more accurately.</p>
            <div className={styles.optionsGrid}>
              {BIOLOGICAL_SEX_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.optionCard} ${quizData.biologicalSex === option.value ? styles.optionCardSelected : ''}`}
                  onClick={() => setQuizData({ ...quizData, biologicalSex: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>How old are you?</h2>
            <p className={styles.stepDescription}>Age affects your metabolism and nutritional requirements.</p>
            <div className={styles.numberPickerContainer}>
              <div className={styles.numberPicker}>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, age: Math.max(18, quizData.age - 1) })}
                >
                  −
                </button>
                <span className={styles.numberValue}>{quizData.age}</span>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, age: Math.min(100, quizData.age + 1) })}
                >
                  +
                </button>
              </div>
              <span className={styles.numberUnit}>years old</span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your height?</h2>
            <p className={styles.stepDescription}>Height is used to calculate your ideal weight range.</p>
            <div className={styles.heightContainer}>
              <div className={styles.heightPicker}>
                <label className={styles.pickerLabel}>Feet</label>
                <div className={styles.numberPicker}>
                  <button
                    className={styles.numberButton}
                    onClick={() => setQuizData({ ...quizData, heightFeet: Math.max(3, quizData.heightFeet - 1) })}
                  >
                    −
                  </button>
                  <span className={styles.numberValue}>{quizData.heightFeet}</span>
                  <button
                    className={styles.numberButton}
                    onClick={() => setQuizData({ ...quizData, heightFeet: Math.min(8, quizData.heightFeet + 1) })}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className={styles.heightPicker}>
                <label className={styles.pickerLabel}>Inches</label>
                <div className={styles.numberPicker}>
                  <button
                    className={styles.numberButton}
                    onClick={() => setQuizData({ ...quizData, heightInches: Math.max(0, quizData.heightInches - 1) })}
                  >
                    −
                  </button>
                  <span className={styles.numberValue}>{quizData.heightInches}</span>
                  <button
                    className={styles.numberButton}
                    onClick={() => setQuizData({ ...quizData, heightInches: Math.min(11, quizData.heightInches + 1) })}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your current weight?</h2>
            <p className={styles.stepDescription}>This helps us understand your starting point.</p>
            <div className={styles.numberPickerContainer}>
              <div className={styles.numberPicker}>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, currentWeight: Math.max(50, quizData.currentWeight - 5) })}
                >
                  −
                </button>
                <span className={styles.numberValue}>{quizData.currentWeight}</span>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, currentWeight: Math.min(500, quizData.currentWeight + 5) })}
                >
                  +
                </button>
              </div>
              <span className={styles.numberUnit}>lbs</span>
            </div>
          </div>
        );

      case 5:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your main goal?</h2>
            <p className={styles.stepDescription}>We'll personalize your nutrition plan based on your goal.</p>
            <div className={styles.optionsGrid}>
              {GOAL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.optionCard} ${quizData.goal === option.value ? styles.optionCardSelected : ''}`}
                  onClick={() => setQuizData({ ...quizData, goal: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your target weight?</h2>
            <p className={styles.stepDescription}>Set a realistic goal for yourself.</p>
            <div className={styles.numberPickerContainer}>
              <div className={styles.numberPicker}>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, targetWeight: Math.max(50, quizData.targetWeight - 5) })}
                >
                  −
                </button>
                <span className={styles.numberValue}>{quizData.targetWeight}</span>
                <button
                  className={styles.numberButton}
                  onClick={() => setQuizData({ ...quizData, targetWeight: Math.min(500, quizData.targetWeight + 5) })}
                >
                  +
                </button>
              </div>
              <span className={styles.numberUnit}>lbs</span>
            </div>
          </div>
        );

      case 7:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your activity level?</h2>
            <p className={styles.stepDescription}>This affects your daily calorie needs.</p>
            <div className={styles.optionsGrid}>
              {ACTIVITY_LEVEL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.optionCard} ${quizData.activityLevel === option.value ? styles.optionCardSelected : ''}`}
                  onClick={() => setQuizData({ ...quizData, activityLevel: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 8:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What is your eating style?</h2>
            <p className={styles.stepDescription}>We'll tailor recipe recommendations to your preferences.</p>
            <div className={styles.optionsGrid}>
              {EATING_STYLE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.optionCard} ${quizData.eatingStyle === option.value ? styles.optionCardSelected : ''}`}
                  onClick={() => setQuizData({ ...quizData, eatingStyle: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 9:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Any health considerations?</h2>
            <p className={styles.stepDescription}>Select all that apply. Skip if none.</p>
            <div className={styles.optionsGrid}>
              {HEALTH_CONSIDERATION_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`${styles.optionCard} ${styles.optionCardMulti} ${quizData.healthConsiderations.includes(option) ? styles.optionCardSelected : ''}`}
                  onClick={() => toggleMultiSelect('healthConsiderations', option)}
                >
                  <span className={styles.checkbox}>
                    {quizData.healthConsiderations.includes(option) && <Check size={16} />}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      case 10:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>What are your eating challenges?</h2>
            <p className={styles.stepDescription}>Select all that apply. Skip if none.</p>
            <div className={styles.optionsGrid}>
              {EATING_CHALLENGE_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`${styles.optionCard} ${styles.optionCardMulti} ${quizData.eatingChallenges.includes(option) ? styles.optionCardSelected : ''}`}
                  onClick={() => toggleMultiSelect('eatingChallenges', option)}
                >
                  <span className={styles.checkbox}>
                    {quizData.eatingChallenges.includes(option) && <Check size={16} />}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      case 11:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Any foods you'd like to avoid?</h2>
            <p className={styles.stepDescription}>Select all that apply. Skip if none.</p>
            <div className={styles.optionsGrid}>
              {FOODS_TO_AVOID_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`${styles.optionCard} ${styles.optionCardMulti} ${quizData.foodsToAvoid.includes(option) ? styles.optionCardSelected : ''}`}
                  onClick={() => toggleMultiSelect('foodsToAvoid', option)}
                >
                  <span className={styles.checkbox}>
                    {quizData.foodsToAvoid.includes(option) && <Check size={16} />}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      case 12:
        return (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>Do you have any food allergies?</h2>
            <p className={styles.stepDescription}>Select all that apply. Skip if none.</p>
            <div className={styles.optionsGrid}>
              {FOOD_ALLERGY_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`${styles.optionCard} ${styles.optionCardMulti} ${quizData.foodAllergies.includes(option) ? styles.optionCardSelected : ''}`}
                  onClick={() => toggleMultiSelect('foodAllergies', option)}
                >
                  <span className={styles.checkbox}>
                    {quizData.foodAllergies.includes(option) && <Check size={16} />}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {currentStep > 1 && (
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowLeft size={24} />
          </button>
        )}
        <div className={styles.progressContainer}>
          <span className={styles.progressText}>Step {currentStep} of {TOTAL_STEPS}</span>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {renderStepContent()}

      <div className={styles.footer}>
        {currentStep < TOTAL_STEPS ? (
          <button
            className={styles.nextButton}
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
          </button>
        ) : (
          <button
            className={styles.nextButton}
            onClick={handleComplete}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Complete'}
          </button>
        )}
      </div>
    </div>
  );
}
